import React from 'react'
import { Field } from 'redux-form'
import { makeStyles, Avatar, Tooltip, styled } from '@material-ui/core'
import { AvatarGroup } from '@material-ui/lab'
import moment from 'moment'
import Modal from '../modal/Modal'

import ReactSelect from '../select/ReactSelect'
import { formatDateLabel, extractDayRange } from '../../utils/extractDate'
import CustomTable from '../table/CustomTable'
import MaterialInput from '../shared/MaterialInput'
import AsyncSelect from '../shared/AsyncSelect'
import DatePickerInput from '../shared/DatePickerInput'
import UserAvatar from '../shared/UserAvatar'
import StyledSVG from '../shared/StyledSVG'
import { colors, relationOptions, queuesStatus } from '../../utils/constants'
import { numberFromText } from '../../utils/helpers'
import cameraSVG from '../../assets/icons/camera-icon.svg'
import searchIcon from '../../assets/icons/search-icon.svg'

const useStyles = makeStyles(theme => ({
  search_inputs: {
    margin: '48px 0px',
    display: 'flex',
    columnGap: 32,
    rowGap: 16,
    alignItems: 'flex-end',
    flexWrap: 'wrap'
  },
  search_field: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: 8,
    height: 'fit-content',
    '& .MuiSelect-select.MuiSelect-select': {
      opacity: 0.5
    },
    '& p.label': {
      margin: 0,
      fontSize: 12,
      fontWeight: 600
    }
  },
  material_input_class: {
    width: '100%',
    marginBottom: '0px !important',
    '& .MuiInput-root': {
      width: 250,
      [theme.breakpoints.down('750')]: {
        width: 150
      }
    }
  },
  date: {
    '& .MuiInputAdornment-positionStart': {
      margin: '0px !important'
    },
    '& .MuiIconButton-root': {
      padding: '8px !important'
    },
    '& .MuiGrid-root': {
      width: 110
    },
    '& .MuiInputBase-root': {
      height: '48px !important'
    },
    '& input::placeholder': {
      fontSize: 12
    },
    '& .MuiInputBase-input': {
      fontSize: 12
    }
  },
  async_select_class: {
    width: 180,
    '& .select__control': {
      height: '48px !important',
      minHeight: '48px !important'
    },
    '& .select__placeholder': {
      fontSize: '12px !important'
    },
    [theme.breakpoints.down('1400')]: {
      width: 150
    },
    [theme.breakpoints.down('1000')]: {
      width: 200
    },
    [theme.breakpoints.down('756')]: {
      width: 170
    }
  },
  react_select: {
    width: 170,
    '& .select__control': {
      width: '100%'
    },
    '& .select__placeholder': {
      fontSize: 12
    }
  },
  avatar_wrapper: {
    display: 'flex',
    alignItems: 'center',
    gap: 16,
    '& p': {
      fontWeight: 500,
      fontSize: 16,
      color: '#555555'
    }
  },
  without_photo_preview: {
    fontSize: '14px',
    fontWeight: 400,
    lineHeight: '17px',
    letterSpacing: '0em',
    textAlign: 'left'
  },
  details_modal: {
    fontFamily: 'Montserrat, Source Sans Pro',
    padding: '32px 40px 40px 40px',
    backgroundColor: '#F7F7F7',
    borderRadius: 32,
    width: 765,
    minHeight: 300,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    rowGap: 24
  },
  details_modal_title: {
    margin: 0,
    color: '#386093',
    fontSize: 24,
    fontWeight: 600
  },
  pickup_queue_infos_wrapper: {
    display: 'flex',
    flexWrap: 'wrap',
    columnGap: 56,
    rowGap: 8,
    maxWidth: '85%'
  },
  pickup_queue_info: {
    display: 'flex',
    alignItems: 'center',
    columnGap: 8,
    '& .label': {
      margin: 0,
      color: '#9B9B9B',
      fontSize: 12,
      fontWeight: 400
    },
    '& .value': {
      margin: 0,
      color: '#386093',
      fontSize: 16,
      fontWeight: 500
    }
  },
  photo_preview: {
    position: 'relative',
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    width: 95,
    height: 95,
    borderRadius: 16,
    backgroundColor: '#D9D9D9',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  parent_name_wrapper: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    '& .name': {
      fontWeight: 500,
      fontSize: 20,
      margin: 0
    },
    '& .relationship': {
      fontWeight: 400,
      fontSize: 16,
      margin: 0
    }
  },
  photo_wrapper: {
    display: 'flex',
    alignItems: 'center',
    columnGap: 12
  },
  parent_section: {
    width: '100%',
    display: 'flex',
    justifyContent: 'space-between',
    rowGap: 12
  },
  parent_data_wrapper: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    columnGap: 24,
    rowGap: 12
  },
  parent_data: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: 4,
    '& .label': {
      color: '#9B9B9B',
      margin: 0,
      fontSize: 12,
      fontWeight: 400
    },
    '& .value': {
      margin: 0,
      fontSize: 16,
      fontWeight: 500
    }
  }
}))

const PhotoPreview = styled('div')(({ theme, bgImage }) => ({
  backgroundImage: `url(${bgImage})`
}))

const StatusComponent = styled('div')(({ theme, bgColor }) => ({
  backgroundColor: bgColor,
  width: 120,
  height: 30,
  borderRadius: 56,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: '#FFFFFF',
  fontSize: 14,
  fontWeight: 600
}))

const SearchPickupQueueForm = ({
  schoolId,
  touch,
  gradeValue,
  classroomValue,
  relationshipValue,
  pickupFinalDateValue,
  pickupInitialDateValue,
  authorizationTypeValue,
  getPickupQueue,
  queues,
  change,
  nameValue,
  todayQueues,
  clearPickupQueues,
  statusValue
}) => {
  const classes = useStyles()
  const selectGradeRef = React.useRef()
  const selectClassroomRef = React.useRef()
  const [detailsModal, setDetailsModal] = React.useState({
    open: false,
    data: {}
  })
  const today = new Date()
  const { beginningOfDay, endOfDay } = extractDayRange(today)

  const defaultParams = {
    ...(todayQueues
      ? {
          'q[updated_at_gteq]': beginningOfDay,
          'q[updated_at_lteq]': endOfDay,
          'q[status_not_eq]': '0'
        }
      : {})
  }

  const [searchParams, setSearchParams] = React.useState({})
  const authorizationType = {
    permanent: 'Permanente',
    temporary: 'Temporário'
  }

  const firstPageAfterSearch = React.useRef(false)

  const columns = [
    {
      key: 'child',
      name: 'Aluno(s)',
      width: '25%',
      render: row => {
        return (
          <div className={classes.avatar_wrapper}>
            <AvatarGroup max={5} spacing={1}>
              {row?.pickup_queue_children?.map((pckQueueChild, idx) => {
                const index =
                  numberFromText(pckQueueChild?.child?.name?.slice(0, 2)) %
                  colors.length
                return (
                  <Tooltip
                    key={`child[${idx}]`}
                    title={pckQueueChild?.child?.name}
                    arial-label='avatar-usuario'
                  >
                    <Avatar
                      style={{
                        height: '30px',
                        width: '30px',
                        backgroundColor:
                          // eslint-disable-next-line standard/computed-property-even-spacing
                          colors[index]
                      }}
                      src={pckQueueChild?.child?.avatar_url}
                    >
                      <p className={classes.without_photo_preview}>
                        {pckQueueChild?.child?.name[0]}
                      </p>
                    </Avatar>
                  </Tooltip>
                )
              })}
            </AvatarGroup>
            <p>{row?.pickup_queue_children?.[0]?.child?.name}</p>
          </div>
        )
      }
    },
    {
      key: 'parent',
      name: 'Responsável',
      width: '20%',
      render: row => {
        const parentData = {
          ...(row?.pickup_authorization?.user_id
            ? {
                name: row?.pickup_authorization?.user?.name,
                photo: row?.pickup_authorization?.user?.avatar_url
              }
            : {
                name: row?.pickup_authorization?.name,
                photo: row?.pickup_authorization?.photo_url
              })
        }
        return (
          <div className={classes.avatar_wrapper}>
            <UserAvatar
              avatarStyle={{
                width: 40,
                height: 40
              }}
              withoutUserName
              userName={parentData.name}
              avatarUrl={parentData.photo}
            />
            <p>{parentData.name}</p>
          </div>
        )
      }
    },
    {
      key: 'type',
      name: 'Tipo',
      width: '15%',
      render: row => {
        const isParent = !!row?.pickup_authorization?.user_id
        return (
          <div>
            <p>
              {isParent
                ? 'Responsável autorizado'
                : authorizationType[row?.pickup_authorization?.pickup_type]}
            </p>
          </div>
        )
      }
    },
    {
      key: 'status',
      name: 'Status',
      width: '10%',
      render: row => {
        return (
          <div className={classes.status_component_wrapper}>
            <StatusComponent bgColor={statusProperties(row.status)}>
              {queuesStatus[row.status]}
            </StatusComponent>
          </div>
        )
      }
    },
    {
      key: 'vehicle_plate',
      name: 'Placa',
      width: '10%',
      render: row => {
        return (
          <p>
            {row?.pickup_authorization?.vehicle_plate?.toUpperCase() || '--'}
          </p>
        )
      }
    },
    {
      key: 'authorization_date',
      name: 'Data',
      width: '10%',
      render: row => {
        return <p>{formatDateLabel(row.created_at)}</p>
      }
    },
    {
      key: 'authorization_hour',
      name: 'Hora',
      width: '10%',
      render: row => {
        return <p>{moment(row.created_at).format('HH:mm')}</p>
      }
    },
    {
      key: 'actions',
      name: 'Ação',
      width: '5%',
      render: row => {
        return (
          <StyledSVG
            onClick={() => handleOpenModal(row)}
            src={searchIcon}
            width={24}
            height={24}
          />
        )
      }
    }
  ]

  React.useEffect(() => {
    getPickupQueue({
      page: 1,
      params: {
        ...defaultParams
      }
    })
    return () => {
      clearPickupQueues()
    }
  }, [])

  React.useEffect(
    () => {
      const extractedInitialDate = extractDayRange(pickupInitialDateValue)
      const extractedFinalDate = extractDayRange(pickupFinalDateValue)
      const newState = {
        // grade filter
        ...(gradeValue && !classroomValue
          ? {
              'q[pickup_queue_children_child_classroom_child_classroom_grade_id_eq]': gradeValue
            }
          : {}),
        // classroom filter
        ...(classroomValue
          ? {
              'q[pickup_queue_children_child_classroom_child_classroom_id_eq]': classroomValue
            }
          : {}),
        'q[pickup_authorization_pickup_type_eq]': relationshipValue,
        ...(pickupInitialDateValue
          ? {
              'q[created_at_gteq]': extractedInitialDate.beginningOfDay,
              'q[created_at_lteq]': pickupFinalDateValue
                ? extractedFinalDate.endOfDay
                : extractedInitialDate.endOfDay
            }
          : {}),
        ...(authorizationTypeValue ?? {}),
        'q[status_eq]': statusValue
      }
      setSearchParams(newState)
      getPickupQueue({
        page: 1,
        params: {
          ...getSearchByNameParam(nameValue),
          ...newState,
          ...defaultParams
        }
      })
    },
    [
      gradeValue,
      classroomValue,
      relationshipValue,
      pickupFinalDateValue,
      pickupInitialDateValue,
      statusValue,
      authorizationTypeValue
    ]
  )

  const statusProperties = status => {
    switch (status) {
      case 'finished': {
        return '#9DD2DA'
      }
      case 'pending': {
        return '#F4A87C'
      }
      case 'exited': {
        return '#EF7C8F'
      }
      default:
        return ''
    }
  }

  const getSearchByNameParam = name => {
    return {
      'q[pickup_queue_children_child_name_cont]': name
    }
  }
  const handleChange = (event, input) => {
    const newValues = Array.isArray(event)
      ? event?.map(v => v.value)
      : event ? event.value : ''
    change(input.name, newValues)
  }

  const handleSearchByName = event => {
    const fieldValue = event?.target?.value
    getPickupQueue({
      page: 1,
      params: {
        ...searchParams,
        ...getSearchByNameParam(fieldValue),
        ...defaultParams
      }
    })
    firstPageAfterSearch.current = true
  }

  const handleCloseModal = () => {
    setDetailsModal(prev => ({ ...prev, open: false }))
  }

  const handleOpenModal = pickupData => {
    const user_id = pickupData?.user_id
    const user = pickupData?.user
    const pickup_authorization = pickupData?.pickup_authorization
    const relationship_type = pickup_authorization
      ? relationOptions.find(
          item => item.value === pickup_authorization?.relationship_type
        ).label
      : user?.child_parents
          .map(
            item =>
              relationOptions.find(
                relation => relation.value === item?.relationship_type
              )?.label
          )
          .filter(item => !!item)
          .join(', ')
    const responsibleData = {
      name: user_id ? user?.name : pickup_authorization?.name,
      photo_url: user_id ? user?.avatar_url : pickup_authorization.photo_url,
      taxpayer_number: user_id
        ? user?.taxpayer_number
        : pickup_authorization?.taxpayer_number,
      phone: user_id ? user?.phone : pickup_authorization?.phone,
      vehicle_plate: pickup_authorization?.vehicle_plate,
      relationship_type
    }

    const queueData = {
      date: `${moment(pickupData.updated_at).format('DD/MM/YYYY')} - ${moment(
        pickupData.updated_at
      ).format('HH')}h${moment(pickupData.updated_at).format('mm')}`,
      approved_by: pickupData?.approved_by?.name,
      pickup_childrens: pickupData?.pickup_queue_children.map(pckChild => {
        const classroom = pckChild.classrooms?.[0]
        return `${pckChild.child?.name} ${
          classroom ? `- ${classroom?.name} (${classroom?.year})` : ''
        }`
      })
    }
    setDetailsModal(prev => ({
      ...prev,
      open: true,
      data: { ...responsibleData, ...queueData }
    }))
  }

  return (
    <div>
      <div className={classes.search_inputs}>
        <div className={classes.search_field}>
          <p className='label'>Busca</p>
          <Field
            rootClass={classes.material_input_class}
            component={MaterialInput}
            name='name'
            onKeyPress={e => {
              if (e.key === 'Enter') {
                e.preventDefault()
                handleSearchByName(e)
              }
            }}
            endBtnFunction={handleSearchByName}
            placeholder='Buscar pelo nome do aluno'
            type='text'
            autoComplete='off'
          />
        </div>
        <div className={`${classes.search_field}`}>
          <p className='label'>Série</p>
          <Field
            component={AsyncSelect}
            rootStyle={classes.async_select_class}
            defaultOptions={false}
            isClearable
            id='grade_id'
            name='grade_id'
            placeholder='Selecione a(s) série(s)'
            selectRef={selectGradeRef}
            handleAsyncChange={handleChange}
            searchParam='q[name_cont]'
            request={{
              path: 'grade',
              params: {
                'q[classrooms_school_id_in]': schoolId
              }
            }}
            touch={touch}
          />
        </div>
        <div className={classes.search_field}>
          <p className='label'>Turma</p>
          <Field
            component={AsyncSelect}
            rootStyle={classes.async_select_class}
            cacheUniqs={[gradeValue]}
            defaultOptions={false}
            isClearable
            id='classroom_id'
            name='classroom_id'
            placeholder='Selecione a(s) turma(s)'
            selectRef={selectClassroomRef}
            handleAsyncChange={handleChange}
            searchParam='q[name_cont]'
            request={{
              path: 'classroom',
              params: {
                'q[grade_id_in]': gradeValue,
                'q[school_id_in]': schoolId,
                'q[year_lteq]': new Date().getFullYear() + 1,
                'q[year_gteq]': new Date().getFullYear()
              }
            }}
            touch={touch}
          />
        </div>
        <div className={`${classes.search_field}`}>
          <p className='label'>Tipo de autorização</p>
          <Field
            component={ReactSelect}
            id='authorization_type'
            name='authorization_type'
            componentClass={classes.react_select}
            isClearable
            placeholder='Selecione a autorização'
            handleChange={handleChange}
            defaultValue={authorizationTypeValue}
            options={[
              {
                label: 'Responsável autorizado',
                value: { 'q[pickup_authorization_user_id_null]': false }
              },
              {
                label: 'Autorização permanente',
                value: {
                  'q[pickup_authorization_pickup_type_eq]': 1,
                  'q[pickup_authorization_user_id_null]': true
                }
              },
              {
                label: 'Autorização temporária',
                value: { 'q[pickup_authorization_pickup_type_eq]': 0 }
              }
            ]}
          />
        </div>
        {!todayQueues && (
          <div className={`${classes.search_field}`}>
            <p className='label'>Status</p>
            <Field
              component={ReactSelect}
              id='status'
              name='status'
              componentClass={classes.react_select}
              isClearable
              placeholder='Selecione o status'
              handleChange={handleChange}
              defaultValue={statusValue}
              options={[
                { label: 'Pendente', value: '0' },
                {
                  label: 'Finalizado',
                  value: '1'
                },
                {
                  label: 'Desistência',
                  value: '2'
                }
              ]}
            />
          </div>
        )}
        {!todayQueues && (
          <>
            <div className={`${classes.search_field} ${classes.date}`}>
              <p className='label'>Data inicial</p>
              <Field
                component={DatePickerInput}
                id='pickup_date_initial'
                name='pickup_date_initial'
                placeholder='00/00/00'
                disableToolbar
              />
            </div>
            <div className={`${classes.search_field} ${classes.date}`}>
              <p className='label'>Data final</p>
              <Field
                component={DatePickerInput}
                disabled={!pickupInitialDateValue}
                id='pickup_date_final'
                name='pickup_date_final'
                placeholder='00/00/00'
                disableToolbar
              />
            </div>
          </>
        )}
      </div>
      <CustomTable
        columns={columns}
        from={'pickup_queue'}
        data={queues.items}
        params={{
          ...defaultParams,
          ...searchParams,
          ...getSearchByNameParam(nameValue)
        }}
        fetchItems={getPickupQueue}
        isFetching={queues.isFetching}
        pagination={queues.pagination}
        columnsPointer={[0]}
        border={'none'}
        headCellRadius
        firstPageAfterSearch={firstPageAfterSearch}
        headColor={'rgba(173, 184, 204, 0.3)'}
      />
      <Modal
        open={detailsModal.open}
        handleClose={handleCloseModal}
        disablePadding
      >
        <div className={classes.details_modal}>
          <h1 className={classes.details_modal_title}>Detalhes da retirada</h1>
          <div className={classes.pickup_queue_infos_wrapper}>
            {[
              { value: 'date', label: 'Data:' },
              { value: 'approved_by', label: 'Entregue por:' },
              { value: 'pickup_childrens', label: 'Aluno(s):' }
            ].map(item => {
              const canShow = !!detailsModal?.data[item.value]
              return (
                canShow && (
                  <div className={classes.pickup_queue_info}>
                    <p className='label'>{item.label}</p>
                    <p className='value'>{detailsModal?.data[item.value]}</p>
                  </div>
                )
              )
            })}
          </div>
          <div className={classes.parent_section}>
            <div className={classes.photo_wrapper}>
              {detailsModal.data?.photo_url ? (
                <PhotoPreview
                  bgImage={detailsModal.data.photo_url}
                  className={classes.photo_preview}
                />
              ) : (
                <div className={classes.photo_preview}>
                  <StyledSVG src={cameraSVG} width={50} height={50} />
                </div>
              )}
              <div className={classes.parent_name_wrapper}>
                <h1 className='name'>{detailsModal?.data?.name}</h1>
                <p className='relationship'>
                  {detailsModal?.data?.relationship_type}
                </p>
              </div>
            </div>
            <div className={classes.parent_data_wrapper}>
              {detailsModal?.data['taxpayer_number'] && (
                <div className={classes.parent_data}>
                  <p className='label'>CPF</p>
                  <p className='value'>
                    {detailsModal?.data['taxpayer_number']}
                  </p>
                </div>
              )}
              {detailsModal?.data['phone'] && (
                <div className={classes.parent_data}>
                  <p className='label'>Telefone</p>
                  <p className='value'>{detailsModal?.data['phone']}</p>
                </div>
              )}
              {detailsModal?.data['vehicle_plate'] && (
                <div className={classes.parent_data}>
                  <p className='label'>Placa</p>
                  <p className='value'>{detailsModal?.data['vehicle_plate']}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </Modal>
    </div>
  )
}

export default SearchPickupQueueForm
